const encoder = new TextEncoder()
const decoder = new TextDecoder()

export const KDF_ITERATIONS = 600_000
export const DEFAULT_POLICY: PasswordPolicy = {
  length: 20,
  lowercase: true,
  uppercase: true,
  digits: true,
  symbols: true,
  excludeAmbiguous: true,
  customExclusions: '',
}

const POOLS = {
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  digits: '0123456789',
  symbols: '!@#$%^&*()-_=+[]{}:,.?',
}
const AMBIGUOUS = new Set('Il1O0o')
const ENVELOPE_AAD = encoder.encode('pass-creator/envelope/v1')

export interface PasswordPolicy {
  length: number
  lowercase: boolean
  uppercase: boolean
  digits: boolean
  symbols: boolean
  excludeAmbiguous: boolean
  customExclusions: string
}

export interface ProjectRecord {
  id: string
  name: string
  account: string
  website: string
  notes: string
  tags: string[]
  archived: boolean
  counter: number
  policy: PasswordPolicy
  createdAt: string
  updatedAt: string
}

export interface VaultSettings {
  theme: 'system' | 'light' | 'dark'
  autoLockMinutes: number
  clearClipboardSeconds: number
}

export interface VaultPayload {
  version: 1
  generationSeed: string
  projects: ProjectRecord[]
  settings: VaultSettings
}

export interface VaultEnvelope {
  schema: 1
  kdf: {
    name: 'PBKDF2-SHA-256'
    iterations: number
    salt: string
  }
  cipher: {
    name: 'AES-256-GCM'
    iv: string
    data: string
  }
  updatedAt: string
}

export interface VaultSession {
  payload: VaultPayload
  key: CryptoKey
  kdf: VaultEnvelope['kdf']
}

function randomBytes(length: number) {
  return crypto.getRandomValues(new Uint8Array(length))
}

function bytesToBase64(bytes: Uint8Array) {
  let value = ''
  for (const byte of bytes) value += String.fromCharCode(byte)
  return btoa(value)
}

function base64ToBytes(value: string) {
  const binary = atob(value)
  return Uint8Array.from(binary, (character) => character.charCodeAt(0))
}

function asArrayBuffer(bytes: Uint8Array) {
  return Uint8Array.from(bytes).buffer
}

async function deriveEncryptionKey(masterPassword: string, salt: Uint8Array, iterations: number) {
  const password = encoder.encode(masterPassword)
  const source = await crypto.subtle.importKey('raw', password, 'PBKDF2', false, ['deriveBits'])
  const root = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: asArrayBuffer(salt), iterations },
    source,
    256,
  )
  password.fill(0)
  const rootBytes = new Uint8Array(root)
  const hkdf = await crypto.subtle.importKey('raw', rootBytes, 'HKDF', false, ['deriveKey'])
  rootBytes.fill(0)
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: asArrayBuffer(salt),
      info: encoder.encode('pass-creator/vault-encryption/v1'),
    },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

async function deriveGenerationKey(seed: Uint8Array) {
  const source = await crypto.subtle.importKey('raw', asArrayBuffer(seed), 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: encoder.encode('pass-creator/generation-salt/v1'),
      info: encoder.encode('pass-creator/password-generation/v1'),
    },
    source,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign'],
  )
}

function emptyPayload(): VaultPayload {
  return {
    version: 1,
    generationSeed: bytesToBase64(randomBytes(32)),
    projects: [],
    settings: { theme: 'system', autoLockMinutes: 5, clearClipboardSeconds: 30 },
  }
}

export async function encryptVault(payload: VaultPayload, key: CryptoKey, kdf: VaultEnvelope['kdf']) {
  const iv = randomBytes(12)
  const plaintext = encoder.encode(JSON.stringify(payload))
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: ENVELOPE_AAD },
    key,
    plaintext,
  )
  plaintext.fill(0)
  return {
    schema: 1,
    kdf,
    cipher: { name: 'AES-256-GCM', iv: bytesToBase64(iv), data: bytesToBase64(new Uint8Array(ciphertext)) },
    updatedAt: new Date().toISOString(),
  } satisfies VaultEnvelope
}

export async function createVault(masterPassword: string, iterations = KDF_ITERATIONS) {
  if (masterPassword.length < 12) throw new Error('主密码至少需要 12 个字符。')
  if (iterations < 1_000 || iterations > 5_000_000) throw new Error('KDF 参数超出允许范围。')
  const salt = randomBytes(16)
  const kdf = {
    name: 'PBKDF2-SHA-256',
    iterations,
    salt: bytesToBase64(salt),
  } satisfies VaultEnvelope['kdf']
  const key = await deriveEncryptionKey(masterPassword, salt, iterations)
  const payload = emptyPayload()
  return { session: { payload, key, kdf } satisfies VaultSession, envelope: await encryptVault(payload, key, kdf) }
}

export function validateEnvelope(value: unknown): asserts value is VaultEnvelope {
  if (!value || typeof value !== 'object') throw new Error('备份文件不是有效对象。')
  const envelope = value as Partial<VaultEnvelope>
  if (envelope.schema !== 1 || envelope.kdf?.name !== 'PBKDF2-SHA-256' || envelope.cipher?.name !== 'AES-256-GCM') {
    throw new Error('不支持的备份格式。')
  }
  const iterations = envelope.kdf.iterations
  if (!Number.isInteger(iterations) || iterations! < 1_000 || iterations! > 5_000_000) throw new Error('KDF 参数无效。')
  if (base64ToBytes(envelope.kdf.salt).length !== 16 || base64ToBytes(envelope.cipher.iv).length !== 12) {
    throw new Error('备份文件的加密参数无效。')
  }
  if (typeof envelope.cipher.data !== 'string' || envelope.cipher.data.length < 24 || envelope.cipher.data.length > 7_000_000) {
    throw new Error('备份文件的数据长度无效。')
  }
}

function validatePayload(value: unknown): asserts value is VaultPayload {
  if (!value || typeof value !== 'object') throw new Error('保险库数据无效。')
  const payload = value as Partial<VaultPayload>
  if (payload.version !== 1 || !Array.isArray(payload.projects) || payload.projects.length > 10_000) {
    throw new Error('保险库版本无效。')
  }
  if (typeof payload.generationSeed !== 'string' || base64ToBytes(payload.generationSeed).length !== 32) {
    throw new Error('保险库生成种子无效。')
  }
  if (!payload.settings || !['system', 'light', 'dark'].includes(payload.settings.theme)) {
    throw new Error('保险库设置无效。')
  }
}

export async function unlockVault(masterPassword: string, input: unknown): Promise<VaultSession> {
  validateEnvelope(input)
  const salt = base64ToBytes(input.kdf.salt)
  const key = await deriveEncryptionKey(masterPassword, salt, input.kdf.iterations)
  try {
    const plaintext = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(input.cipher.iv), additionalData: ENVELOPE_AAD },
      key,
      base64ToBytes(input.cipher.data),
    )
    const payload = JSON.parse(decoder.decode(plaintext)) as unknown
    validatePayload(payload)
    return { payload, key, kdf: input.kdf }
  } catch {
    throw new Error('主密码错误，或保险库文件已损坏。')
  }
}

export async function changeMasterPassword(payload: VaultPayload, masterPassword: string, iterations = KDF_ITERATIONS) {
  if (masterPassword.length < 12) throw new Error('新主密码至少需要 12 个字符。')
  const salt = randomBytes(16)
  const kdf = {
    name: 'PBKDF2-SHA-256',
    iterations,
    salt: bytesToBase64(salt),
  } satisfies VaultEnvelope['kdf']
  const key = await deriveEncryptionKey(masterPassword, salt, iterations)
  return { session: { payload, key, kdf } satisfies VaultSession, envelope: await encryptVault(payload, key, kdf) }
}

function selectedPools(policy: PasswordPolicy) {
  const exclusions = new Set(policy.customExclusions.normalize('NFKC'))
  if (policy.excludeAmbiguous) for (const character of AMBIGUOUS) exclusions.add(character)
  return (Object.keys(POOLS) as Array<keyof typeof POOLS>)
    .filter((key) => policy[key])
    .map((key) => [...POOLS[key]].filter((character) => !exclusions.has(character)).join(''))
}

export function validatePolicy(policy: PasswordPolicy) {
  if (!Number.isInteger(policy.length) || policy.length < 12 || policy.length > 64) return '长度必须在 12 到 64 之间。'
  if (policy.customExclusions.length > 128) return '自定义排除字符不能超过 128 个。'
  const pools = selectedPools(policy)
  if (!pools.length) return '至少选择一种字符类型。'
  if (pools.some((pool) => !pool.length)) return '排除规则清空了某个已启用的字符类型。'
  if (policy.length < pools.length) return '密码长度不足以包含所有字符类型。'
  return ''
}

class HmacByteStream {
  private block = 0
  private buffer = new Uint8Array()
  private offset = 0

  constructor(private readonly key: CryptoKey, private readonly context: string) {}

  async next() {
    if (this.offset >= this.buffer.length) {
      const bytes = await crypto.subtle.sign('HMAC', this.key, encoder.encode(`${this.context}|${this.block++}`))
      this.buffer = new Uint8Array(bytes)
      this.offset = 0
    }
    return this.buffer[this.offset++]
  }

  async index(length: number) {
    const limit = 256 - (256 % length)
    let byte = await this.next()
    while (byte >= limit) byte = await this.next()
    return byte % length
  }
}

function normalized(value: string) {
  return value.normalize('NFKC').trim().toLocaleLowerCase('en-US')
}

export async function generatePassword(
  generationSeed: string,
  project: string,
  account: string,
  counter: number,
  policy: PasswordPolicy,
) {
  const error = validatePolicy(policy)
  if (error) throw new Error(error)
  if (!project.trim()) throw new Error('项目名称不能为空。')
  if (!Number.isInteger(counter) || counter < 1 || counter > 999_999) throw new Error('轮换计数无效。')

  const pools = selectedPools(policy)
  const alphabet = pools.join('')
  const exclusions = [...new Set(policy.customExclusions.normalize('NFKC'))].sort().join('')
  const context = JSON.stringify({
    app: 'pass-creator',
    version: 1,
    project: normalized(project),
    account: normalized(account),
    counter,
    policy: {
      length: policy.length,
      lowercase: policy.lowercase,
      uppercase: policy.uppercase,
      digits: policy.digits,
      symbols: policy.symbols,
      excludeAmbiguous: policy.excludeAmbiguous,
      customExclusions: exclusions,
    },
  })
  const key = await deriveGenerationKey(base64ToBytes(generationSeed))
  const stream = new HmacByteStream(key, context)
  const characters: string[] = []

  for (const pool of pools) characters.push(pool[await stream.index(pool.length)])
  while (characters.length < policy.length) characters.push(alphabet[await stream.index(alphabet.length)])
  for (let index = characters.length - 1; index > 0; index--) {
    const swap = await stream.index(index + 1)
    ;[characters[index], characters[swap]] = [characters[swap], characters[index]]
  }
  return characters.join('')
}

export function estimateBits(policy: PasswordPolicy) {
  const pools = selectedPools(policy)
  const size = pools.join('').length
  return size ? Math.floor(policy.length * Math.log2(size)) : 0
}

export function createProject(name: string, account = ''): ProjectRecord {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    name: name.trim(),
    account: account.trim(),
    website: '',
    notes: '',
    tags: [],
    archived: false,
    counter: 1,
    policy: { ...DEFAULT_POLICY },
    createdAt: now,
    updatedAt: now,
  }
}
